import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  Button,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  OpenInNew as OpenInNewIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../WebSocketContext';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '60vw',
    height: '70vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'wrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const FileContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f0f0f0',
  padding: '32px',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
  },
}));

const FileIcon = styled(DescriptionIcon)(({ theme }) => ({
  fontSize: '120px',
  color: primaryColor,
  opacity: 0.7,
  marginBottom: '24px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '90px',
    marginBottom: '16px',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: '150px',
  padding: '12px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: '600',
  margin: '8px',
  '&.download-btn': {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  '&.open-btn': {
    backgroundColor: primaryColor,
    color: 'white',
    '&:hover': {
      backgroundColor: '#4a4a9a',
    },
  },
}));

const OtherViewer = React.forwardRef(({
  open,
  onClose,
  filename = '',
  eventId = null,
  filePath = '',
  allowServerOpen = true
}, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [fileBlob, setFileBlob] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [totalChunks, setTotalChunks] = useState(0);
  const containerRef = useRef(null);
  const chunksRef = useRef([]);
  const { subscribeToMessages, ws } = useWebSocket();

  const matchesMessage = useCallback((data = {}) => {
    const msgName = (data.filename || data.name || '').toLowerCase();
    const msgPath = (data.path || '').toLowerCase();
    const fn = (filename || '').toLowerCase();

    if (eventId && data.event_id === eventId) return true;

    if (!eventId) {
      if (fn) {
        if (msgName && msgName === fn) return true;
        if (msgPath && msgPath.endsWith(fn)) return true;
      }
      if (!msgName && !msgPath) return true;
    }

    return false;
  }, [eventId, filename]);

  // Cleanup blob URL when component unmounts or dialog closes
  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setFileBlob(null);
      setLoading(false);
      setProgress(0);
      setError(null);
      setTotalChunks(0);
      chunksRef.current = [];
    } else {
      // When dialog opens, reset state
      setFileBlob(null);
      setLoading(false);
      setProgress(0);
      setError(null);
      setTotalChunks(0);
      chunksRef.current = [];
    }
  }, [open]);

  // Handle fileUrl cleanup separately
  useEffect(() => {
    if (!open && fileUrl) {
      URL.revokeObjectURL(fileUrl);
      setFileUrl(null);
    }
  }, [open, fileUrl]);

  const assembleFile = useCallback((chunks) => {
    try {
      // Combine all chunks into a single Uint8Array
      let totalLength = 0;
      chunks.forEach(chunk => {
        totalLength += chunk.length;
      });

      const combined = new Uint8Array(totalLength);
      let offset = 0;
      chunks.forEach(chunk => {
        combined.set(chunk, offset);
        offset += chunk.length;
      });

      // Determine MIME type based on file extension
      const extension = filename.toLowerCase().split('.').pop();
      let mimeType = 'application/octet-stream'; // default binary
      
      // Common MIME types for different file extensions
      const mimeTypes = {
        'txt': 'text/plain',
        'csv': 'text/csv',
        'xml': 'application/xml',
        'json': 'application/json',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed',
        '7z': 'application/x-7z-compressed',
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'mp4': 'video/mp4',
        'avi': 'video/x-msvideo',
        'mov': 'video/quicktime',
      };

      if (mimeTypes[extension]) {
        mimeType = mimeTypes[extension];
      }

      // Create blob and URL
      const blob = new Blob([combined], { type: mimeType });
      const url = URL.createObjectURL(blob);
      
      setFileBlob(blob);
      setFileUrl(url);
      setLoading(false);
    } catch (error) {
      console.error('Error assembling file:', error);
      setError('Failed to assemble file');
      setLoading(false);
    }
  }, [filename]);

  // Handle file stream messages (if needed for download functionality)
  const handleFileStreamMessage = useCallback((message) => {
    switch (message.status) {
      case 'file_stream_start':
        if (matchesMessage(message)) {
          setLoading(true);
          setProgress(0);
          setError(null);
          setTotalChunks(message.total_chunks || 0);
          chunksRef.current = [];
        }
        break;

      case 'file_stream_chunk':
        if (matchesMessage(message)) {
          try {
            // Decode base64 chunk
            const binaryString = atob(message.chunk_data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            // Store chunk in ref
            const chunkIndex = message.chunk_number;
            if (!chunksRef.current[chunkIndex]) {
              chunksRef.current[chunkIndex] = bytes;
              
              // Update progress
              const receivedChunks = chunksRef.current.filter(chunk => chunk).length;
              if (totalChunks > 0) {
                setProgress((receivedChunks / totalChunks) * 100);
              }
            }

            // If this is the last chunk, assemble the file
            if (message.is_last_chunk) {
              setTimeout(() => {
                const validChunks = chunksRef.current.filter(chunk => chunk);
                
                if (validChunks.length > 0) {
                  assembleFile(validChunks);
                } else {
                  setError('Failed to assemble file - no chunks received');
                  setLoading(false);
                }
              }, 100);
            }
          } catch (error) {
            console.error('Error processing file chunk:', error);
            setError('Failed to process file chunk');
          }
        }
        break;

      case 'file_stream_complete':
        if (matchesMessage(message)) {
          // Final assembly if not already done
          if (!fileUrl) {
            const validChunks = chunksRef.current.filter(chunk => chunk);
            if (validChunks.length > 0) {
              assembleFile(validChunks);
            }
          }
        }
        break;

      case 'error':
        if (matchesMessage(message)) {
          setError(message.message || 'Failed to load file');
          setLoading(false);
        }
        break;

      default:
        // No action needed for other statuses
        break;
    }
  }, [matchesMessage, totalChunks, fileUrl, assembleFile]);

  useEffect(() => {
    if (!open) return;

    const unsubscribe = subscribeToMessages(['openfile'], (parsedMessage) => {
      // Only process file-related messages for this specific request
      if (parsedMessage.data &&
          ['file_stream_start', 'file_stream_chunk', 'file_stream_complete', 'error'].includes(parsedMessage.data.status) &&
          matchesMessage(parsedMessage.data)) {
        handleFileStreamMessage(parsedMessage.data);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [open, subscribeToMessages, handleFileStreamMessage, matchesMessage]);

  // Expose handleFileStreamMessage through ref
  React.useImperativeHandle(ref, () => ({
    handleFileStreamMessage
  }), [handleFileStreamMessage]);

  const downloadFile = () => {
    if (fileBlob) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // If no blob is available, request file for download
      setLoading(true);
      if (ws && eventId) {
        ws.send(JSON.stringify({
          command: "openfile",
          id: eventId,
          download: true
        }));
      } else if (ws && filePath) {
        ws.send(JSON.stringify({
          command: "openfile",
          path: filePath,
          download: true
        }));
      }
    }
  };

  const openFileOnServer = () => {
    if (!allowServerOpen) {
      return;
    }
    if (ws && eventId) {
      ws.send(JSON.stringify({
        command: "openfile",
        id: eventId
      }));
    } else if (ws && filePath) {
      ws.send(JSON.stringify({
        command: "openfile",
        path: filePath
      }));
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Get file extension for display
  const getFileExtension = () => {
    if (!filename) return '';
    const extension = filename.split('.').pop();
    return extension ? extension.toUpperCase() : '';
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <DescriptionIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.document_viewer_title')} - ${filename}` : t('viewer.document_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadFile} 
            size="small"
            title={t('viewer.download_file')}
          >
            <DownloadIcon />
          </IconButton>
          {allowServerOpen && (
            <IconButton 
              onClick={openFileOnServer}
              size="small"
              title="Open with Default Application"
            >
              <OpenInNewIcon />
            </IconButton>
          )}
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mx: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('viewer.loading_file')} {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ flex: 1, maxWidth: 200 }}
            />
          </Box>
        )}
      </ToolbarContainer>

      <DialogContent sx={{ 
        "--dialog-content-top-padding": "0px",
        padding: 0, 
        height: '100%', 
        overflow: 'auto',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(87, 87, 176, 0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(87, 87, 176, 0.4)',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: 'rgba(87, 87, 176, 0.6)',
          },
        },
        // Firefox scrollbar styling
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(87, 87, 176, 0.4) rgba(87, 87, 176, 0.1)',
      }}>
        <FileContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: 300, mb: 2 }}
              />
              <Typography color="text.secondary">
                {t('viewer.loading_file')} {Math.round(progress)}%
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_file')}
              </Typography>
              <Typography color="text.secondary">
                {error}
              </Typography>
            </Box>
          ) : (
            <>
              <FileIcon />
              <Typography variant="h5" gutterBottom sx={{ color: primaryColor, fontWeight: 'bold' }}>
                {filename || 'Unknown File'}
              </Typography>
              {getFileExtension() && (
                <Typography variant="h6" gutterBottom sx={{ color: '#666', mb: 3 }}>
                  {getFileExtension()} File
                </Typography>
              )}
              <Typography variant="body1" sx={{ color: '#888', mb: 4, maxWidth: '400px' }}>
                {allowServerOpen
                  ? 'This file format cannot be previewed in the browser. You can download the file or open it with the default application on your system.'
                  : 'This file format cannot be previewed in the browser. You can download the file.'}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <ActionButton 
                  className="download-btn"
                  variant="contained" 
                  startIcon={<DownloadIcon />}
                  onClick={downloadFile}
                >
                  {t('viewer.download_file')}
                </ActionButton>
                {allowServerOpen && (
                  <ActionButton 
                    className="open-btn"
                    variant="contained" 
                    startIcon={<OpenInNewIcon />}
                    onClick={openFileOnServer}
                  >
                    Open with Default App
                  </ActionButton>
                )}
              </Box>
            </>
          )}
        </FileContainer>
      </DialogContent>
    </StyledDialog>
  );
});

// Set display name for debugging
OtherViewer.displayName = 'OtherViewer';

export default OtherViewer;
